import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '@shared/auth/AuthContext';
import {
  smsMoney,
  useCreateSmsTopUp,
  useRefreshSmsTopUp,
  useSmsSummary,
  useSmsTopUps,
} from '../smsCredit';

export function SmsCreditPanel() {
  const { t } = useIntlTranslations();
  const { isDemo } = useAuthContext();
  const { data, isLoading, error } = useSmsSummary();
  const { data: topUps, error: topUpsError } = useSmsTopUps();
  const create = useCreateSmsTopUp();
  const refresh = useRefreshSmsTopUp();
  const [amount, setAmount] = useState('500');
  const requestKey = useRef(crypto.randomUUID());
  const location = useLocation();
  const returnedRef = new URLSearchParams(location.search).get('smsPayment');
  const checkedReturn = useRef<string | null>(null);
  useEffect(() => {
    if (returnedRef && !isDemo && checkedReturn.current !== returnedRef) {
      checkedReturn.current = returnedRef;
      refresh.mutate(returnedRef);
    }
  }, [returnedRef, isDemo, refresh.mutate]);
  if (isDemo) {
    return <Alert severity="info">{t('smsCredit.demo')}</Alert>;
  }
  if (isLoading) {
    return <CircularProgress size={24} aria-label={t('smsCredit.loading')} />;
  }
  if (error || !data) {
    return <Alert severity="error">{error?.message || t('smsCredit.unavailable')}</Alert>;
  }
  const choose = (value: string) => {
    setAmount(value);
    requestKey.current = crypto.randomUUID();
    create.reset();
  };
  const valid =
    Number.isInteger(Number(amount)) && Number(amount) >= 100 && Number(amount) <= 10000;
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('smsCredit.description')}
        </Typography>
      </Box>
      <Stack spacing={2}>
        <Stack
          direction={'row'}
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {smsMoney(data.balanceHalers)}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t('smsCredit.availableBalance')}
              {data.reservedHalers > 0
                ? t('smsCredit.reservedSuffix', { amount: smsMoney(data.reservedHalers) })
                : ''}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={t('smsCredit.pricePerPart', { price: smsMoney(data.priceHalers) })}
          />
        </Stack>
        <Typography variant="body2">
          {t('smsCredit.forecast', {
            days: data.forecastDays,
            count: data.plannedMessages,
            amount: smsMoney(data.requiredHalers),
            parts: data.plannedParts,
          })}
        </Typography>
        {data.lowCredit && (
          <Alert severity="warning">
            {t('smsCredit.lowCredit', { amount: smsMoney(data.shortfallHalers) })}
          </Alert>
        )}
        {!data.available && <Alert severity="info">{t('smsCredit.sendingInactive')}</Alert>}
        <Stack direction="row" gap={1} flexWrap="wrap">
          {data.presets.map(value => (
            <Button
              size="small"
              key={value}
              disabled={create.isPending}
              variant={Number(amount) === value ? 'contained' : 'outlined'}
              onClick={() => choose(String(value))}
            >
              {smsMoney(value * 100)}
            </Button>
          ))}
          {data.lastTopUpAmount !== null && (
            <Button
              startIcon={<ReplayIcon />}
              disabled={create.isPending}
              onClick={() => choose(String(data.lastTopUpAmount))}
            >
              {t('smsCredit.repeatTopUp', { amount: smsMoney(data.lastTopUpAmount * 100) })}
            </Button>
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label={t('smsCredit.amountLabel')}
            type="number"
            size="small"
            value={amount}
            onChange={event => choose(event.target.value)}
            disabled={create.isPending}
            helperText={t('smsCredit.amountHelp')}
            slotProps={{ htmlInput: { min: 100, max: 10000, step: 1 } }}
          />
          <Button
            variant="contained"
            disabled={!valid || create.isPending}
            onClick={() =>
              create.mutate({ amount: Number(amount), requestKey: requestKey.current })
            }
          >
            {create.isPending
              ? t('smsCredit.openingGateway')
              : t('smsCredit.topUp', { amount: smsMoney(Number(amount || 0) * 100) })}
          </Button>
        </Stack>

        {create.error && <Alert severity="error">{create.error.message}</Alert>}
      </Stack>
      {returnedRef && (
        <Alert severity={refresh.data?.status === 'PAID' ? 'success' : 'info'}>
          {refresh.data?.status === 'PAID'
            ? t('smsCredit.paymentPaid')
            : refresh.data?.status === 'CANCELLED'
              ? t('smsCredit.paymentCancelled')
              : t('smsCredit.paymentPending')}
        </Alert>
      )}
      {refresh.error && <Alert severity="error">{refresh.error.message}</Alert>}
      {topUpsError && <Alert severity="error">{topUpsError.message}</Alert>}
      {!!topUps?.length && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">{t('smsCredit.recentTopUps')}</Typography>
          {topUps.slice(0, 5).map(topUp => (
            <Stack
              key={topUp.id}
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
            >
              <Typography variant="body2">
                {new Date(topUp.createdAt).toLocaleDateString('cs-CZ')} ·{' '}
                {smsMoney(topUp.amountHalers)} ·{' '}
                {topUp.creditedAt
                  ? t('smsCredit.credited')
                  : topUp.status === 'CANCELLED'
                    ? t('smsCredit.cancelled')
                    : t('smsCredit.awaitingPayment')}
              </Typography>
              {!topUp.creditedAt && topUp.status !== 'CANCELLED' && (
                <Stack direction="row" gap={1}>
                  {topUp.redirect && (
                    <Button size="small" href={topUp.redirect}>
                      {t('smsCredit.finishPayment')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    disabled={refresh.isPending}
                    onClick={() => refresh.mutate(topUp.refId)}
                  >
                    {t('smsCredit.verifyPayment')}
                  </Button>
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
